import {
  getResolvedKioskInstallerUrl,
  isAndroidDevice,
  isIosDevice,
  isIosInAppBrowser,
  isMobileDevice,
  canUseNativePwaInstall,
} from "../config/kioskInstall";

type KioskBridge = {
  isKiosk?: boolean;
  close?: () => void;
  systemExit?: () => void;
};

type ElectronBridge = KioskBridge & {
  closeApp?: () => void;
  quit?: () => void;
};

declare global {
  interface Window {
    kioskAPI?: KioskBridge;
    electronAPI?: ElectronBridge;
  }
}

const KIOSK_INSTALL_PROMPT_KEY = "mosida-kiosk-install-prompt-dismissed";

export const isElectronKiosk = (): boolean => {
  const userAgent = navigator.userAgent;
  return (
    userAgent.includes("Electron") ||
    window.electronAPI?.isKiosk === true ||
    window.kioskAPI?.isKiosk === true
  );
};

export const isKioskApp = (): boolean => isElectronKiosk();

export const isStandaloneDisplay = (): boolean => {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone ===
      true
  );
};

export const shouldShowKioskInstallPrompt = (): boolean => {
  if (isElectronKiosk() || isStandaloneDisplay()) {
    return false;
  }

  return sessionStorage.getItem(KIOSK_INSTALL_PROMPT_KEY) !== "true";
};

export const dismissKioskInstallPrompt = (): void => {
  sessionStorage.setItem(KIOSK_INSTALL_PROMPT_KEY, "true");
};

export const getKioskInstallerUrl = (): string => getResolvedKioskInstallerUrl();

export const getKioskInstallInstructions = (): string[] => {
  if (isMobileDevice()) {
    if (isIosInAppBrowser()) {
      return [
        "Open this page in Safari to install the app.",
        'In Safari, tap Share, then "Add to Home Screen".',
        "Tap Add, then launch Mosida from your home screen.",
      ];
    }

    if (isIosDevice()) {
      return [
        "Tap the Share button at the bottom of Safari.",
        'Choose "Add to Home Screen".',
        "Tap Add, then open Mosida from your home screen.",
      ];
    }

    if (isAndroidDevice() && !canUseNativePwaInstall()) {
      return [
        "Use Chrome and open the site over HTTPS to install automatically.",
        "Or open the browser menu (three dots).",
        'Choose "Install app" or "Add to Home screen" to create a Mosida icon.',
      ];
    }

    if (isAndroidDevice()) {
      return [
        'Tap "Add to Home Screen" to create a Mosida app icon.',
        "The icon will appear on your home screen and app drawer.",
        'If no prompt appears, open Chrome menu → "Install app".',
      ];
    }

    return [
      "Add this app to your home screen for fullscreen kiosk mode.",
      "Use your browser menu to install or add to home screen.",
    ];
  }

  const userAgent = navigator.userAgent;

  if (/Windows/i.test(userAgent)) {
    return [
      "Download the Mosida Giftshop Kiosk installer for Windows.",
      "Run the installer and open Mosida Giftshop Kiosk from the desktop.",
      "The app will launch in fullscreen kiosk mode.",
    ];
  }

  if (/Macintosh|Mac OS X/i.test(userAgent)) {
    return [
      "Download the Mosida Giftshop Kiosk app for macOS.",
      "Open the .dmg file and drag Mosida Giftshop Kiosk to Applications.",
      "Launch the app to enter fullscreen kiosk mode.",
    ];
  }

  return [
    "Install the Mosida Giftshop Kiosk desktop app on this device.",
    "Open the installed app to use fullscreen kiosk mode.",
  ];
};

/** Quit the Electron/Android kiosk shell and return to the device home screen. */
export const closeKioskApp = (): void => {
  const tryClose = (fn?: () => void): boolean => {
    if (typeof fn !== "function") return false;
    try {
      fn();
      return true;
    } catch {
      return false;
    }
  };

  // Prefer full process quit so kiosk mode does not leave a blank window.
  if (tryClose(window.kioskAPI?.systemExit)) return;
  if (tryClose(window.electronAPI?.quit)) return;
  if (tryClose(window.kioskAPI?.close)) return;
  if (tryClose(window.electronAPI?.close)) return;
  if (tryClose(window.electronAPI?.closeApp)) return;

  try {
    window.close();
  } catch {
    // Browser/PWA may block window.close().
  }
};
